// src/pages/Community/Missions/MissionDetails.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageWrapper from "../../../components/PageWrapper";
import "./css/MissionDetail.css";
import FeedbackModal from "../components/FeedbackModal";
import MissionDetailContent from "./MissionDetailContent";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { UserName } from "../../../context/profileUI";
import MissionForm from "./MissionForm"; // adjust path if needed
import Modal from "../components/Modal";

// Improved YouTube embed helper: tolerates raw IDs, youtu.be, embed/ and watch?v= forms
function toYouTubeEmbed(u = "") {
  if (!u) return null;
  try {
    // If it's likely a raw ID (no spaces, short-ish), return embed URL
    const idCandidate = u.trim();
    if (!idCandidate.includes(" ") && /^[A-Za-z0-9_-]{6,}$/.test(idCandidate) && !/\:|\//.test(idCandidate)) {
      return `https://www.youtube.com/embed/${idCandidate}`;
    }

    // normalize short links
    let normalized = u.replace("youtu.be/", "youtube.com/watch?v=");
    // ensure protocol for URL parsing
    if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;
    const url = new URL(normalized);

    // common query param
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;

    // fallback: last path segment (handles /embed/ID or /v/ID)
    const segments = url.pathname.split("/").filter(Boolean);
    const last = segments.pop();
    if (last && /^[A-Za-z0-9_-]{6,}$/.test(last)) return `https://www.youtube.com/embed/${last}`;

    return null;
  } catch {
    return null;
  }
}

export default function MissionDetail({ user: userFromRoute }) {
  const { id: missionId } = useParams();
  const location = useLocation();

  // ----- state + refs + hooks (ALL hooks must be declared before any early return) -----
  const [authUser, setAuthUser] = useState(null);
  const user = userFromRoute ?? authUser ?? null; // source of truth
  const uid = user?.uid ?? null;

  const [editOpen, setEditOpen] = useState(false);
  const [m, setM] = useState(undefined); // undefined = loading, null = not found
  const [completion, setCompletion] = useState(null); // user's mission progress
  const [linkCopied, setLinkCopied] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [fbOpen, setFbOpen] = useState(false);
  const [coverUrl, setCoverUrl] = useState(null);
  const [galleryUrls, setGalleryUrls] = useState([]);
  const basePath = useMemo(() => ["missions", missionId], [missionId]);
  const bookmarkKey = useMemo(() => `missions:${missionId}`, [missionId]);
  const [featuredReaction, setFeaturedReaction] = useState(null);
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);
  const fromIndexState = location.state;

  // Auth listener
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setAuthUser(u || null));
    return () => unsub();
  }, []);

  // Firestore mission doc listener
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "missions", missionId), (snap) => {
      setM(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub();
  }, [missionId]);

  // Firestore user completion listener
  useEffect(() => {
    if (!uid) {
      setCompletion(null);
      return;
    }
    const compRef = doc(db, "missions", missionId, "completions", uid);
    const unsub = onSnapshot(compRef, (snap) => {
      setCompletion(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [missionId, uid]);

  // Share timeout cleanup
  const shareTimeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      if (shareTimeoutRef.current) {
        clearTimeout(shareTimeoutRef.current);
        shareTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!m) return;

    const storage = getStorage();

    async function loadImages() {
      try {
        // Cover
        if (m.coverImage) {
          const coverRef = ref(storage, m.coverImage);
          const url = await getDownloadURL(coverRef);
          setCoverUrl(url);
        } else {
          setCoverUrl(null);
        }

        // Gallery
        if (Array.isArray(m.gallery)) {
          const urls = await Promise.all(
            m.gallery.map(async (imgPath) => {
              const imgRef = ref(storage, imgPath);
              return await getDownloadURL(imgRef);
            })
          );
          setGalleryUrls(urls);
        } else {
          setGalleryUrls([]);
        }
      } catch (err) {
        console.error("Firebase image error:", err);
      }
    }

    loadImages();
  }, [m]);

  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname;
    try {
      if (navigator.share) {
        await navigator.share({ title: m?.missionName ?? "Mission", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
      shareTimeoutRef.current = setTimeout(() => {
        setLinkCopied(false);
        shareTimeoutRef.current = null;
      }, 1800);
    } catch {
      // best-effort; ignore errors
    }
  }, [m?.missionName, location.pathname]);

  // Lightbox helpers (non-hook functions)
  const openLightbox = (index) => {
    if (!galleryUrls.length) return;
    setLightbox({ open: true, index });
  };
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const nextLight = (e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = galleryUrls.length;
      return { open: true, index: (s.index + 1) % len };
    });
  };
  const prevLight = (e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = galleryUrls.length;
      return { open: true, index: (s.index - 1 + len) % len };
    });
  };

  // keyboard handling for lightbox (hook placed before returns)
  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevLight(e);
      else if (e.key === "ArrowRight") nextLight(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, /* prevLight/nextLight are stable here (closures) */]);

  // derived YouTube embed URL (hook placed before returns)
  const yt = useMemo(() => (m?.videoUrl ? toYouTubeEmbed(m.videoUrl) : null), [m?.videoUrl]);

  // ------------------------------------------------------------------------------------

  const canEdit =
    !!uid &&
    !!m &&
    (m.createdBy === uid ||
      (Array.isArray(m.allowEditBy) && m.allowEditBy.includes(uid)) ||
      m.isAdminEditable === true);

  const visibilityLabel = useMemo(() => {
    if (!m) return null;
    if (typeof m.isPrivate === "boolean") return m.isPrivate ? "Private" : "Public";
    return null;
  }, [m]);

  // early returns (now safely after all hooks)
  if (m === undefined) {
    return (
      <PageWrapper>
        <div className="msn-wrap">
          <div className="msn-skel-card" aria-busy="true" aria-live="polite">
            <div className="msn-skel-row">
              <div className="msn-skel-chip" />
              <div className="msn-skel-chip" />
              <div className="msn-skel-chip" />
            </div>
            <div className="msn-skel-title" />
            <div className="msn-skel-sub" />
            <div className="msn-skel-hero" />
            <div className="msn-skel-section" />
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (m === null) {
    return (
      <PageWrapper>
        <div className="msn-wrap">
          <div className="msn-empty-card" role="alert">
            <div className="msn-empty-title">We can’t find that mission</div>
            <p className="msn-empty-sub">
              It may have been removed or the link is incorrect.
            </p>
            <Link to="/missions" className="msn-btn msn-btn--primary">
              ← Back to missions
            </Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const hasObjectives =
    (m.objectives?.primary?.length ?? 0) > 0 ||
    (m.objectives?.optional?.length ?? 0) > 0;

  // --- derived progress numbers (for % bar) ---
  const primaryCount = m.objectives?.primary?.length ?? 0;
  const optionalCount = m.objectives?.optional?.length ?? 0;
  const totalObjectives = primaryCount + optionalCount;

  // Normalize checklist: supports boolean arrays or index arrays
  function normalizeChecklist(arr, total) {
    if (!Array.isArray(arr) || total <= 0)
      return { doneIdx: [], doneSet: new Set() };

    // boolean flags: [true,false,true,...]
    if (typeof arr[0] === "boolean") {
      const idx = arr
        .map((v, i) => (v ? i : null))
        .filter((i) => i !== null && i < total);
      return { doneIdx: idx, doneSet: new Set(idx) };
    }

    // index array: [0,2,3]
    const idx = arr
      .filter((n) => Number.isInteger(n))
      .filter((n) => n >= 0 && n < total);
    return { doneIdx: idx, doneSet: new Set(idx) };
  }

  const primaryChecklist = normalizeChecklist(
    completion?.checklist?.primary,
    primaryCount
  );
  const optionalChecklist = normalizeChecklist(
    completion?.checklist?.optional,
    optionalCount
  );

  const donePrimary = primaryChecklist.doneIdx.length;
  const doneOptional = optionalChecklist.doneIdx.length;
  const doneAll = donePrimary + doneOptional;

  const pct =
    totalObjectives > 0
      ? Math.round((doneAll / totalObjectives) * 100)
      : completion?.completed
        ? 100
        : 0;

  return (
    <PageWrapper>
      <div className="msn-wrap">
        {/* Action / breadcrumb row */}
        <div className="msn-detail-toprow">
          <Link
            to="/missions"
            state={
              fromIndexState?.fromIndex
                ? {
                  game: fromIndexState.game,
                  filter: fromIndexState.filter,
                }
                : undefined
            }
            className="msn-back"
            aria-label="Back to missions"
          >
            ← All missions
          </Link>

          <div className="msn-actions">
            {m.videoUrl && (
              <a
                className="msn-action"
                href={m.videoUrl}
                target="_blank"
                rel="noreferrer"
              >
                ▶ Open video
              </a>
            )}
            <button className="msn-action" onClick={onShare}>
              {linkCopied ? "✓ Link copied" : "⤴ Share"}
            </button>
            <button className="msn-action" onClick={() => setFbOpen(true)}>
              💬 Feedback
            </button>

            {/* ✏️ Edit button (visible to creator) */}
            {canEdit && (
              <button
                className="msn-action"
                onClick={() => setEditOpen(true)}
                aria-label="Edit this mission"
                title="Edit this mission"
              >
                ✎ Edit
              </button>
            )}
          </div>
        </div>

        {/* Header */}
        <MissionDetailContent
          m={m}
          uid={uid}
          coverUrl={coverUrl}
          galleryUrls={galleryUrls}
          visibilityLabel={visibilityLabel}
          featuredReaction={featuredReaction}
          selectedReactionFromBar={selectedReactionFromBar}

          completion={completion}
          yt={yt}
          onShare={onShare}
          linkCopied={linkCopied}
          canEdit={canEdit}
          setEditOpen={setEditOpen}
          openLightbox={openLightbox}

          hasObjectives={hasObjectives}
          primaryChecklist={primaryChecklist}
          optionalChecklist={optionalChecklist}
          primaryCount={primaryCount}
          optionalCount={optionalCount}
          donePrimary={donePrimary}
          doneOptional={doneOptional}
          doneAll={doneAll}
          totalObjectives={totalObjectives}
          pct={pct}

          /* NEW */
          presenceKey="missions"
          missionId={missionId}
          basePath={basePath}
          bookmarkKey={bookmarkKey}
          onReactionSelect={setSelectedReactionFromBar}
        />


      </div>

      {/* Lightbox */}
      {lightbox.open && galleryUrls.length > 0 && (
        <div
          className="msn-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="msn-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ✕
          </button>
          <button
            className="msn-lightbox-nav left"
            onClick={prevLight}
            aria-label="Previous"
          >
            ‹
          </button>
          <img
            className="msn-lightbox-img"
            src={galleryUrls[lightbox.index]}
            alt={`Screenshot ${lightbox.index + 1}`}
          />
          <button
            className="msn-lightbox-nav right"
            onClick={nextLight}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      )}

      <FeedbackModal
        open={fbOpen}
        onClose={() => setFbOpen(false)}
        mission={m}
        sourceComponent="MissionDetails"
        source={location.pathname}
      />

      <Modal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Mission"
        size="lg"
        closeOnBackdrop={true}
        showClose={true}
      >
        {m && (
          <MissionForm
            user={user}
            missionId={missionId}
            initial={m}
            onSaved={(id) => {
              console.log("Mission updated:", id);
              setEditOpen(false);
            }}
            onCancel={() => setEditOpen(false)}
            mode="edit"
          />
        )}
      </Modal>
    </PageWrapper>
  );
}
